import { useState } from "react";
import {Mail, Lock, KeyRound, Send, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, ShieldCheck, Trash2, } from 'lucide-react';
import toast from "react-hot-toast";
import { lumi } from "@/lib/lumi";
import { emailAdmin_AccountDeletionRequest } from "@/lib/emails";

/**
 * AccountSettings — composant réutilisable pour l'onglet « Mon compte »
 * des dashboards candidat / recruteur / organisme.
 *
 * - Affiche l'adresse email du compte en LECTURE SEULE (input grisé/désactivé).
 * - Permet de changer le mot de passe via le flow OTP du SDK Lumi :
 *     1) sendOtp({ email, type: 'resetPassword' })
 *     2) resetPassword({ email, newPassword, otp })
 *
 * Le SDK Lumi ne propose pas de méthode "change password en étant connecté"
 * directe : l'envoi d'un OTP à l'adresse du compte est la voie officielle
 * et sécurisée pour changer son mot de passe.
 */

interface AccountSettingsProps {
  email: string;
  /** Couleur d'accent (boutons, focus). Défaut: bleu marine. */
  accent?: "blue" | "red" | "amber";
  /** Type de compte (recruiter / organisme / candidate) — joint à la demande de suppression. */
  accountType?: string;
}

const ACCENT_CLASSES: Record<
  NonNullable<AccountSettingsProps["accent"]>,
  {
    btn: string;
    btnSoft: string;
    ring: string;
    text: string;
    bgSoft: string;
    border: string;
  }
> = {
  blue: {
    btn: "bg-blue-600 hover:bg-blue-700 text-white",
    btnSoft: "bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200",
    ring: "focus:ring-blue-500",
    text: "text-blue-700",
    bgSoft: "bg-blue-50",
    border: "border-blue-200",
  },
  red: {
    btn: "bg-red-600 hover:bg-red-700 text-white",
    btnSoft: "bg-red-50 hover:bg-red-100 text-red-700 border border-red-200",
    ring: "focus:ring-red-500",
    text: "text-red-700",
    bgSoft: "bg-red-50",
    border: "border-red-200",
  },
  amber: {
    btn: "bg-amber-600 hover:bg-amber-700 text-white",
    btnSoft: "bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200",
    ring: "focus:ring-amber-500",
    text: "text-amber-700",
    bgSoft: "bg-amber-50",
    border: "border-amber-200",
  },
};

export default function AccountSettings({ email, accent = "blue", accountType }: AccountSettingsProps) {
  const colors = ACCENT_CLASSES[accent];

  // ─── Suppression de compte ────────────────────────────────────────────────
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteSent, setDeleteSent] = useState(false);

  // Étapes du flow : idle -> otp_sent -> done
  const [step, setStep] = useState<"idle" | "otp_sent" | "done">("idle");

  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [sendingOtp, setSendingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0); // secondes restantes avant re-envoi

  // Décompte du cooldown OTP (60s)
  const startCooldown = (seconds: number) => {
    setCooldown(seconds);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    if (!email) {
      toast.error("Adresse email du compte introuvable.");
      return;
    }
    setSendingOtp(true);
    try {
      await lumi.auth.sendOtp({ email, type: "resetPassword" });
      setStep("otp_sent");
      startCooldown(60);
      toast.success(`Code envoyé à ${email}. Vérifiez votre boîte mail.`);
    } catch (err) {
      console.error("[AccountSettings] sendOtp failed", err);
      toast.error("Impossible d'envoyer le code. Réessayez dans un instant.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (cooldown > 0) return;
    await handleSendOtp();
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      toast.error("Saisissez le code reçu par email.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setSubmitting(true);
    try {
      await lumi.auth.resetPassword({
        email,
        newPassword,
        otp: otp.trim(),
      });
      setStep("done");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Mot de passe mis à jour avec succès !");
    } catch (err: unknown) {
      console.error("[AccountSettings] resetPassword failed", err);
      const msg =
        (err as { message?: string })?.message?.toLowerCase() ||
        "";
      if (msg.includes("otp") || msg.includes("code")) {
        toast.error("Code invalide ou expiré. Renvoyez un nouveau code.");
      } else {
        toast.error("Impossible de mettre à jour le mot de passe.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = () => {
    setStep("idle");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSubmitDeletion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteConfirm.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error('Veuillez saisir le mot "SUPPRIMER" pour confirmer.');
      return;
    }
    setDeleteSubmitting(true);
    try {
      let userId: string | undefined;
      try {
        userId = (lumi.auth.user as { userId?: string } | null)?.userId;
      } catch {
        userId = undefined;
      }
      await emailAdmin_AccountDeletionRequest({
        userEmail: email,
        userId,
        reason: deleteReason.trim() || undefined,
        accountType,
      });
      setDeleteSent(true);
      toast.success("Demande envoyée. Notre équipe vous contactera sous 7 jours.");
    } catch (err) {
      console.error("[AccountSettings] account deletion request failed", err);
      toast.error("Impossible d'envoyer la demande. Réessayez ou contactez le support.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* En-tête */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Mon compte</h2>
        <p className="text-sm text-gray-600">
          Gérez les informations de connexion liées à votre compte sur Emplois BNSSA &amp; MNS.
        </p>
      </div>

      {/* Bloc Email — lecture seule */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 mb-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
            <Mail className="w-4 h-4" />
          </span>
          <h3 className="text-base font-bold text-gray-900">Adresse email</h3>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          L'adresse email associée à votre compte ne peut pas être modifiée.
        </p>
        <div className="relative">
          <input
            type="email"
            value={email || "—"}
            disabled
            readOnly
            aria-label="Adresse email du compte (non modifiable)"
            className="w-full px-3 py-2.5 pr-10 border border-gray-200 rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed select-all"
          />
          <Lock
            className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Bloc Mot de passe */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`w-8 h-8 rounded-lg ${colors.bgSoft} ${colors.text} flex items-center justify-center`}
          >
            <KeyRound className="w-4 h-4" />
          </span>
          <h3 className="text-base font-bold text-gray-900">Mot de passe</h3>
        </div>

        {step === "done" ? (
          <div
            className={`flex items-start gap-3 ${colors.bgSoft} ${colors.border} border rounded-lg p-4`}
          >
            <CheckCircle2
              className={`w-5 h-5 flex-shrink-0 mt-0.5 ${colors.text}`}
            />
            <div className="text-sm">
              <p className={`font-bold mb-0.5 ${colors.text}`}>
                Mot de passe mis à jour
              </p>
              <p className="text-gray-700">
                Votre nouveau mot de passe est désormais actif. Vous resterez
                connecté sur cet appareil.
              </p>
              <button
                type="button"
                onClick={handleRestart}
                className="mt-3 text-xs font-semibold underline text-gray-700 hover:text-gray-900"
              >
                Changer de nouveau le mot de passe
              </button>
            </div>
          </div>
        ) : step === "idle" ? (
          <>
            <p className="text-xs text-gray-500 mb-4">
              Pour des raisons de sécurité, un code de vérification à usage
              unique sera envoyé à votre adresse email avant tout changement de
              mot de passe.
            </p>
            <button
              type="button"
              onClick={handleSendOtp}
              disabled={sendingOtp || !email}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.btn}`}
            >
              {sendingOtp ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sendingOtp ? "Envoi en cours…" : "Envoyer un code par email"}
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmitNewPassword} className="space-y-4">
            <div className={`flex items-start gap-2 ${colors.bgSoft} ${colors.border} border rounded-lg p-3 text-xs`}>
              <ShieldCheck className={`w-4 h-4 flex-shrink-0 mt-0.5 ${colors.text}`} />
              <p className="text-gray-700">
                Un code à 6 chiffres a été envoyé à{" "}
                <strong className="text-gray-900">{email}</strong>. Saisissez-le
                ci-dessous avec votre nouveau mot de passe.
              </p>
            </div>

            {/* OTP */}
            <div>
              <label
                htmlFor="otp"
                className="block text-xs font-semibold text-gray-700 mb-1"
              >
                Code reçu par email *
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\s/g, ""))}
                placeholder="123456"
                maxLength={10}
                className={`w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 ${colors.ring} outline-none tracking-widest font-mono`}
              />
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={cooldown > 0 || sendingOtp}
                className="mt-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed underline"
              >
                {cooldown > 0
                  ? `Renvoyer un code dans ${cooldown}s`
                  : sendingOtp
                  ? "Envoi…"
                  : "Renvoyer un code"}
              </button>
            </div>

            {/* Nouveau mot de passe */}
            <div>
              <label
                htmlFor="newPassword"
                className="block text-xs font-semibold text-gray-700 mb-1"
              >
                Nouveau mot de passe * <span className="font-normal text-gray-500">(8 caractères minimum)</span>
              </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  className={`w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 ${colors.ring} outline-none`}
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  aria-label={showNew ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-900"
                >
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirmation */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-xs font-semibold text-gray-700 mb-1"
              >
                Confirmer le mot de passe *
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  className={`w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 ${colors.ring} outline-none`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-900"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="mt-1 text-[11px] text-red-600 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={
                  submitting ||
                  !otp.trim() ||
                  newPassword.length < 8 ||
                  newPassword !== confirmPassword
                }
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.btn}`}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                {submitting ? "Mise à jour…" : "Mettre à jour le mot de passe"}
              </button>
              <button
                type="button"
                onClick={handleRestart}
                className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>

      {/* ── Suppression de compte (lien discret) ───────────────────────────── */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        {!deleteOpen && !deleteSent && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 underline underline-offset-4 decoration-gray-300 hover:decoration-red-400 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
              Supprimer mon compte
            </button>
          </div>
        )}

        {deleteOpen && !deleteSent && (
          <div className="bg-red-50/40 border border-red-200 rounded-xl p-5 sm:p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="w-8 h-8 rounded-lg bg-red-100 text-red-700 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </span>
              <div>
                <h3 className="text-base font-bold text-gray-900">Supprimer mon compte</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Cette action est définitive. Vos candidatures, dossiers, profils et alertes seront supprimés sous 7 jours, conformément au RGPD.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmitDeletion} className="space-y-4">
              <div>
                <label
                  htmlFor="deleteReason"
                  className="block text-xs font-semibold text-gray-700 mb-1"
                >
                  Motif (optionnel)
                </label>
                <textarea
                  id="deleteReason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Aidez-nous à nous améliorer en nous indiquant la raison de votre départ."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none resize-none"
                />
                <p className="mt-1 text-[11px] text-gray-500">{deleteReason.length}/500</p>
              </div>

              <div>
                <label
                  htmlFor="deleteConfirm"
                  className="block text-xs font-semibold text-gray-700 mb-1"
                >
                  Pour confirmer, saisissez <strong className="text-red-700">SUPPRIMER</strong> *
                </label>
                <input
                  id="deleteConfirm"
                  type="text"
                  autoComplete="off"
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none font-mono tracking-widest"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={
                    deleteSubmitting ||
                    deleteConfirm.trim().toUpperCase() !== "SUPPRIMER"
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deleteSubmitting ? "Envoi…" : "Envoyer ma demande de suppression"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteOpen(false);
                    setDeleteReason("");
                    setDeleteConfirm("");
                  }}
                  className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {deleteSent && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-bold text-green-800 mb-0.5">Demande de suppression envoyée</p>
              <p className="text-gray-700">
                Nous avons bien reçu votre demande. Votre compte et l'ensemble des données associées seront supprimés sous 7 jours. Vous recevrez une confirmation par email à <strong>{email}</strong>.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
